import React, { useState } from 'react';
import styled from 'styled-components';
import { Container } from '../components/common/Container';
import { Link } from 'react-router-dom';

const DocsContainer = styled.div`
  padding: ${({ theme }) => theme.spacing['2xl']} 0;
  min-height: 80vh;
`;

const DocsLayout = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  align-items: start;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.aside`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border-radius: ${({ theme }) => theme.radii.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  position: sticky;
  top: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    position: static;
  }
`;

const SidebarSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SidebarTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  font-weight: 600;
`;

const SidebarLink = styled.a<{ active?: boolean }>`
  display: block;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  color: ${({ theme, active }) => active ? theme.colors.primary : theme.colors.text.primary};
  text-decoration: none;
  border-radius: ${({ theme }) => theme.radii.md};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.primary};
  }

  ${({ active, theme }) => active && `
    background: ${theme.colors.surface};
    font-weight: 500;
  `}
`;

const Content = styled.main`
  max-width: 800px;
`;

const DocSection = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};
  scroll-margin-top: ${({ theme }) => theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  font-weight: 700;
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-weight: 600;
`;

const SubsectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  font-weight: 600;
`;

const Paragraph = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.base};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.7;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const CodeBlock = styled.pre`
  background: ${({ theme }) => theme.colors.code.background};
  border: 1px solid ${({ theme }) => theme.colors.code.border};
  border-radius: ${({ theme }) => theme.radii.md};
  padding: ${({ theme }) => theme.spacing.md};
  overflow-x: auto;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-family: ${({ theme }) => theme.fonts.code};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  line-height: 1.5;
`;

const InlineCode = styled.code`
  background: ${({ theme }) => theme.colors.code.background};
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.radii.sm};
  font-family: ${({ theme }) => theme.fonts.code};
  font-size: 0.9em;
  color: ${({ theme }) => theme.colors.primary};
`;

const List = styled.ul`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding-left: ${({ theme }) => theme.spacing.lg};
`;

const ListItem = styled.li`
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.7;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const Alert = styled.div<{ type?: 'info' | 'warning' | 'success' }>`
  background: ${({ theme, type }) => {
    switch (type) {
      case 'warning': return '#FEF3C7';
      case 'success': return '#D1FAE5';
      default: return '#DBEAFE';
    }
  }};
  border-left: 4px solid ${({ theme, type }) => {
    switch (type) {
      case 'warning': return theme.colors.warning;
      case 'success': return theme.colors.success;
      default: return theme.colors.primary;
    }
  }};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.radii.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const DocsPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState('getting-started');

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <DocsContainer>
      <Container>
        <DocsLayout>
          <Sidebar>
            <SidebarSection>
              <SidebarTitle>Getting Started</SidebarTitle>
              <SidebarLink 
                active={activeSection === 'getting-started'} 
                onClick={() => scrollToSection('getting-started')}
              >
                Overview
              </SidebarLink>
              <SidebarLink 
                active={activeSection === 'fossil-fills'} 
                onClick={() => scrollToSection('fossil-fills')}
              >
                Fossil Fills
              </SidebarLink>
              <SidebarLink 
                active={activeSection === 'prompt-improver'} 
                onClick={() => scrollToSection('prompt-improver')}
              >
                Prompt Improver
              </SidebarLink>
              <SidebarLink 
                active={activeSection === 'extension'} 
                onClick={() => scrollToSection('extension')}
              >
                Browser Extension
              </SidebarLink>
            </SidebarSection>

            <SidebarSection>
              <SidebarTitle>Configuration</SidebarTitle>
              <SidebarLink 
                active={activeSection === 'authentication'} 
                onClick={() => scrollToSection('authentication')}
              >
                Authentication
              </SidebarLink>
              <SidebarLink 
                active={activeSection === 'api-setup'} 
                onClick={() => scrollToSection('api-setup')}
              >
                API Setup
              </SidebarLink>
              <SidebarLink 
                active={activeSection === 'categories'} 
                onClick={() => scrollToSection('categories')}
              >
                User Categories
              </SidebarLink>
              <SidebarLink 
                active={activeSection === 'system-prompts'} 
                onClick={() => scrollToSection('system-prompts')}
              >
                System Prompts
              </SidebarLink>
            </SidebarSection>

            <SidebarSection>
              <SidebarTitle>Advanced</SidebarTitle>
              <SidebarLink 
                active={activeSection === 'self-hosting'} 
                onClick={() => scrollToSection('self-hosting')}
              >
                Self-Hosting
              </SidebarLink>
              <SidebarLink 
                active={activeSection === 'api-reference'} 
                onClick={() => scrollToSection('api-reference')}
              >
                API Reference
              </SidebarLink>
              <SidebarLink 
                active={activeSection === 'troubleshooting'} 
                onClick={() => scrollToSection('troubleshooting')}
              >
                Troubleshooting
              </SidebarLink>
            </SidebarSection>
          </Sidebar>

          <Content>
            <DocSection id="getting-started">
              <Title>Promptasaurus Documentation</Title>
              <Paragraph>
                Welcome to Promptasaurus! A Gruntwork.ai project that provides a comprehensive suite of tools
                to enhance your interactions with ChatGPT, Claude, and other language models.
              </Paragraph>
              
              <SectionTitle>What is Promptasaurus?</SectionTitle>
              <Paragraph>
                Promptasaurus offers multiple AI-powered tools to help you write better prompts. From our innovative
                Fossil Fills Mad Libs system to our intelligent prompt improver and upcoming browser extension,
                we help you get better results from AI models through thoughtful prompt engineering.
              </Paragraph>

              <SubsectionTitle>Key Features</SubsectionTitle>
              <List>
                <ListItem>🚀 One-click prompt improvement</ListItem>
                <ListItem>🎯 Smart role assignment for better context</ListItem>
                <ListItem>✨ Two-stage improvement process</ListItem>
                <ListItem>🔒 Privacy-first with local API option</ListItem>
                <ListItem>🎨 Customizable for your specific needs</ListItem>
              </List>
            </DocSection>

            <DocSection id="installation">
              <SectionTitle>Installation</SectionTitle>
              
              <SubsectionTitle>Browser Extension</SubsectionTitle>
              <Paragraph>
                The easiest way to use Promptasaurus is through our browser extension:
              </Paragraph>
              
              <List>
                <ListItem>1. The extension is currently in development - visit our Extension page to join the waitlist</ListItem>
                <ListItem>2. Click the extension icon in your browser toolbar</ListItem>
                <ListItem>3. Configure your settings (optional)</ListItem>
                <ListItem>4. Start improving prompts on ChatGPT or Claude!</ListItem>
              </List>

              <SubsectionTitle>Manual Installation (Developer Mode)</SubsectionTitle>
              <CodeBlock>{`1. Clone the repository:
   git clone https://github.com/gruntwork/promptasaurus.git

2. Open Chrome and navigate to chrome://extensions/

3. Enable "Developer mode" in the top right

4. Click "Load unpacked" and select the browser_extension folder

5. The Promptasaurus icon will appear in your toolbar`}</CodeBlock>
            </DocSection>

            <DocSection id="fossil-fills">
              <SectionTitle>Fossil Fills - Mad Libs for AI Prompts</SectionTitle>
              <Paragraph>
                Fossil Fills is our innovative approach to prompt creation. Like Mad Libs, you fill in blanks
                to create comprehensive, well-structured prompts tailored to your specific needs.
              </Paragraph>
              
              <SubsectionTitle>How to Use Fossil Fills</SubsectionTitle>
              <List>
                <ListItem>1. Navigate to the Fossil Fills page from the main menu</ListItem>
                <ListItem>2. Choose a template category or start with a custom prompt</ListItem>
                <ListItem>3. Fill in the blanks with your specific context and requirements</ListItem>
                <ListItem>4. Preview the complete prompt in real-time</ListItem>
                <ListItem>5. Copy the result and use it in your favorite AI tool</ListItem>
              </List>

              <SubsectionTitle>Available Templates</SubsectionTitle>
              <List>
                <ListItem><strong>Content Creation:</strong> Blog posts, articles, social media content</ListItem>
                <ListItem><strong>Code & Development:</strong> Functions, debugging, documentation</ListItem>
                <ListItem><strong>Business:</strong> Reports, analysis, presentations</ListItem>
                <ListItem><strong>Education:</strong> Lesson plans, explanations, exercises</ListItem>
              </List>

              <Alert type="info">
                <strong>Tip:</strong> Each template includes educational tooltips to help you understand
                what makes an effective prompt!
              </Alert>
            </DocSection>

            <DocSection id="prompt-improver">
              <SectionTitle>Prompt Improver</SectionTitle>
              <Paragraph>
                Our intelligent prompt improvement engine transforms basic prompts into comprehensive instructions
                using a sophisticated two-stage enhancement process.
              </Paragraph>
              
              <SubsectionTitle>How It Works</SubsectionTitle>
              <List>
                <ListItem>1. Paste or type your original prompt</ListItem>
                <ListItem>2. Click the "Improve" button</ListItem>
                <ListItem>3. Our AI analyzes and enhances your prompt</ListItem>
                <ListItem>4. Review the improved version with added context and structure</ListItem>
                <ListItem>5. Copy and use the enhanced prompt</ListItem>
              </List>

              <Alert type="info">
                <strong>Pro Tip:</strong> The improver works best when you provide some initial context.
                Even a basic prompt will be transformed into something much more effective!
              </Alert>
            </DocSection>

            <DocSection id="extension">
              <SectionTitle>Browser Extension</SectionTitle>
              <Paragraph>
                Our browser extension (currently in development) will bring Promptasaurus directly to your
                favorite AI interfaces like ChatGPT and Claude.
              </Paragraph>
              
              <SubsectionTitle>Planned Features</SubsectionTitle>
              <List>
                <ListItem>One-click prompt improvement without leaving the AI interface</ListItem>
                <ListItem>Smart context detection based on your conversation</ListItem>
                <ListItem>Template library access</ListItem>
                <ListItem>Privacy-first local processing</ListItem>
                <ListItem>Custom keyboard shortcuts</ListItem>
              </List>

              <Alert type="info">
                <strong>Status:</strong> The extension is actively being developed. Visit our Extension page
                to join the waitlist and be notified when it launches!
              </Alert>
            </DocSection>

            <DocSection id="authentication">
              <SectionTitle>Authentication & Accounts</SectionTitle>
              <Paragraph>
                Promptasaurus offers optional user accounts for saving preferences, custom templates,
                and accessing premium features when they become available.
              </Paragraph>
              
              <SubsectionTitle>Creating an Account</SubsectionTitle>
              <List>
                <ListItem>1. Click "Get Started" in the navigation menu</ListItem>
                <ListItem>2. Enter your email and create a secure password</ListItem>
                <ListItem>3. Verify your email address</ListItem>
                <ListItem>4. Access your personalized dashboard</ListItem>
              </List>

              <SubsectionTitle>Account Benefits</SubsectionTitle>
              <List>
                <ListItem>Save and organize custom Fossil Fills templates</ListItem>
                <ListItem>Track your prompt improvement history</ListItem>
                <ListItem>Sync settings across devices</ListItem>
                <ListItem>Early access to new features</ListItem>
                <ListItem>Future access to Raptor and Rex tier features</ListItem>
              </List>
            </DocSection>

            <DocSection id="api-setup">
              <SectionTitle>API Setup</SectionTitle>
              
              <SubsectionTitle>Backend Architecture</SubsectionTitle>
              <Paragraph>
                Promptasaurus uses a modern FastAPI backend that supports multiple AI providers including
                OpenAI and Anthropic. The API handles prompt improvement, template management, and authentication.
              </Paragraph>

              <SubsectionTitle>Running the Development Server</SubsectionTitle>
              <CodeBlock>{`# Clone the repository
git clone https://github.com/gruntwork/promptasaurus.git

# Install backend dependencies
cd backend
pip install -r requirements.txt

# Set up environment variables
export OPENAI_API_KEY="your-openai-key"
export ANTHROPIC_API_KEY="your-anthropic-key"

# Start the development server
python prompt_server.py

# API will be available at http://localhost:8000`}</CodeBlock>

              <SubsectionTitle>Custom API Endpoint</SubsectionTitle>
              <Paragraph>
                You can configure a custom API endpoint in the extension settings:
              </Paragraph>
              
              <List>
                <ListItem>1. Click the Promptasaurus extension icon</ListItem>
                <ListItem>2. Enter your custom API endpoint URL</ListItem>
                <ListItem>3. Click "Test Connection" to verify</ListItem>
                <ListItem>4. The extension will use your custom endpoint</ListItem>
              </List>

              <Alert type="warning">
                <strong>Privacy Note:</strong> When using a custom endpoint, ensure you trust the server
                as your prompts will be sent there for processing.
              </Alert>
            </DocSection>

            <DocSection id="categories">
              <SectionTitle>User Categories</SectionTitle>
              <Paragraph>
                Promptasaurus can tailor improvements based on your role. Select your category in the
                extension settings to get role-specific enhancements:
              </Paragraph>

              <SubsectionTitle>Available Categories</SubsectionTitle>
              <List>
                <ListItem><strong>Business Analyst:</strong> Focus on requirements, stakeholders, and business value</ListItem>
                <ListItem><strong>Screen Writer:</strong> Emphasis on narrative, character development, and dialogue</ListItem>
                <ListItem><strong>Developer:</strong> Technical accuracy, code examples, and implementation details</ListItem>
              </List>

              <Paragraph>
                More categories coming soon! Each category adjusts the improvement strategy to match
                your specific needs and communication style.
              </Paragraph>
            </DocSection>

            <DocSection id="system-prompts">
              <SectionTitle>System Prompts</SectionTitle>
              <Paragraph>
                Advanced users can customize how Promptasaurus improves prompts by providing a custom
                system prompt. This allows you to:
              </Paragraph>

              <List>
                <ListItem>Define specific formatting preferences</ListItem>
                <ListItem>Add domain-specific terminology</ListItem>
                <ListItem>Set tone and style guidelines</ListItem>
                <ListItem>Include company-specific requirements</ListItem>
              </List>

              <SubsectionTitle>Example System Prompt</SubsectionTitle>
              <CodeBlock>{`When improving prompts, always:
- Use formal business language
- Include specific metrics and KPIs
- Reference our company values
- Format with clear sections and bullet points`}</CodeBlock>
            </DocSection>

            <DocSection id="self-hosting">
              <SectionTitle>Self-Hosting</SectionTitle>
              <Paragraph>
                For maximum privacy and control, you can self-host the Promptasaurus API:
              </Paragraph>

              <SubsectionTitle>Requirements</SubsectionTitle>
              <List>
                <ListItem>Python 3.8 or higher</ListItem>
                <ListItem>OpenAI API key (or compatible API)</ListItem>
                <ListItem>4GB RAM minimum</ListItem>
                <ListItem>Optional: Docker for containerized deployment</ListItem>
              </List>

              <SubsectionTitle>Docker Deployment</SubsectionTitle>
              <CodeBlock>{`# Build the Docker image
docker build -t promptasaurus-api ./backend

# Run with environment variables
docker run -p 8000:8000 \
  -e OPENAI_API_KEY="your-openai-key" \
  -e ANTHROPIC_API_KEY="your-anthropic-key" \
  -e JWT_SECRET="your-secret-key" \
  promptasaurus-api

# API available at http://localhost:8000`}</CodeBlock>

              <SubsectionTitle>Production Deployment</SubsectionTitle>
              <Paragraph>
                For production deployments, we provide Terraform configurations for AWS:
              </Paragraph>
              <CodeBlock>{`# Navigate to infrastructure
cd infrastructure/terraform

# Initialize Terraform
terraform init

# Review the deployment plan
terraform plan -var-file="terraform.tfvars.prd"

# Deploy to AWS
terraform apply -var-file="terraform.tfvars.prd"`}</CodeBlock>

              <SubsectionTitle>Production Deployment</SubsectionTitle>
              <Paragraph>
                For production use, we recommend:
              </Paragraph>
              <List>
                <ListItem>Using HTTPS with a valid SSL certificate</ListItem>
                <ListItem>Implementing rate limiting</ListItem>
                <ListItem>Adding authentication if exposed to the internet</ListItem>
                <ListItem>Monitoring API usage and performance</ListItem>
              </List>
            </DocSection>

            <DocSection id="api-reference">
              <SectionTitle>API Reference</SectionTitle>
              
              <SubsectionTitle>POST /improve</SubsectionTitle>
              <Paragraph>
                Main endpoint for prompt improvement with context questions.
              </Paragraph>

              <CodeBlock>{`Request:
{
  "prompt": "Write a blog post about AI",
  "use_free_tier": true,
  "user_category": "developer",
  "system_prompt": "Focus on technical accuracy"
}

Response:
{
  "improved_prompt": "As an expert technical writer...",
  "context_questions": [
    {
      "id": "q1",
      "question": "What is the target audience?",
      "suggestions": ["Developers", "Business leaders", "General public"]
    }
  ]
}`}</CodeBlock>

              <SubsectionTitle>POST /api/improve</SubsectionTitle>
              <Paragraph>
                Quick improvement endpoint for browser extension (no context questions).
              </Paragraph>

              <CodeBlock>{`Request:
{
  "prompt": "Explain quantum computing",
  "platform": "chatgpt",
  "user_category": "developer"
}

Response:
{
  "improved_prompt": "You are a quantum computing expert..."
}`}</CodeBlock>
            </DocSection>

            <DocSection id="troubleshooting">
              <SectionTitle>Troubleshooting</SectionTitle>
              
              <SubsectionTitle>Extension Not Working</SubsectionTitle>
              <List>
                <ListItem>Ensure the API server is running (<InlineCode>http://localhost:8017</InlineCode>)</ListItem>
                <ListItem>Check the browser console for errors (F12)</ListItem>
                <ListItem>Verify the extension has proper permissions</ListItem>
                <ListItem>Try reloading the extension</ListItem>
              </List>

              <SubsectionTitle>API Connection Issues</SubsectionTitle>
              <List>
                <ListItem>Test the connection using the "Test Connection" button</ListItem>
                <ListItem>Check firewall settings aren't blocking the port</ListItem>
                <ListItem>Verify the API endpoint URL is correct</ListItem>
                <ListItem>Ensure CORS is properly configured on custom endpoints</ListItem>
              </List>

              <SubsectionTitle>Poor Improvement Results</SubsectionTitle>
              <List>
                <ListItem>Provide more context in your original prompt</ListItem>
                <ListItem>Select the appropriate user category</ListItem>
                <ListItem>Answer the context questions thoroughly</ListItem>
                <ListItem>Consider adding a custom system prompt</ListItem>
              </List>

              <Alert type="info">
                Still having issues? <Link to="/contact">Contact our support team</Link> or 
                check our <a href="https://github.com/gruntwork/promptasaurus/issues" target="_blank" rel="noopener noreferrer">GitHub issues</a>.
              </Alert>
            </DocSection>
          </Content>
        </DocsLayout>
      </Container>
    </DocsContainer>
  );
};